import { createContext, useCallback, useContext, useEffect, useState, ReactNode } from "react";
import { HOME_BASE_URL } from "@config";
import { navigateTo } from "@utils/navigate";
import getToken from "@api/getToken";

export interface AuthContextType {
  token: string | null;
  isAuthLoading: boolean;
  refreshAction: () => Promise<string | null>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType>({
  token: null,
  isAuthLoading: true,
  refreshAction: async () => null,
  logout: () => undefined,
});

export const AuthContextProvider = ({ children }: { children: ReactNode }) => {
  const [token, setToken] = useState<string | null>(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true);

  useEffect(() => {
    const fetchToken = async () => {
      const tokenResponse = await getToken();
      setToken(tokenResponse?.access_token ?? null);
      setIsAuthLoading(false);
    };
    fetchToken();
  }, []);

  const refreshAction = useCallback(async (): Promise<string | null> => {
    const tokenResponse = await getToken();
    setToken(tokenResponse?.access_token ?? null);
    return tokenResponse?.access_token ?? null;
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    navigateTo(`${HOME_BASE_URL}/login?ReturnUrl=${encodeURIComponent(window.location.href)}`);
  }, []);

  return (
    <AuthContext.Provider value={{ token, isAuthLoading, refreshAction, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
